import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "lucide-react";

const EASE = [0.76, 0, 0.24, 1];
const PORTFOLIO_URL = "https://linktr.ee/akankshachandok";

const works = [
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_akanksha-creative/artifacts/l09fmyn5_IMG_0272.jpg",
    title: "Arjun Bijlani",
    kind: "Celebrity Styling",
    year: "2025",
    span: "md:col-span-7",
    ratio: "aspect-[4/5]",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_akanksha-creative/artifacts/sjjxy4to_IMG_0268.jpg",
    title: "Butterfly, in Bloom",
    kind: "Beauty Editorial",
    year: "2025",
    span: "md:col-span-5 md:mt-16",
    ratio: "aspect-[4/5]",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_akanksha-creative/artifacts/8rlu8bau_IMG_0273.jpg",
    title: "Salim Merchant",
    kind: "Concert Styling",
    year: "2025",
    span: "md:col-span-5",
    ratio: "aspect-[4/5]",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_akanksha-creative/artifacts/0ebuiqi0_IMG_0264.jpg",
    title: "B Praak",
    kind: "Artist Portrait",
    year: "2025",
    span: "md:col-span-7 md:mt-16",
    ratio: "aspect-[4/5]",
  },
  {
    src: "https://customer-assets-jai6qajn.emergentagent.net/job_akanksha-creative/artifacts/gt4t54d2_IMG_0265.jpg",
    title: "Krystle D’Souza",
    kind: "Editorial Cover",
    year: "2025",
    span: "md:col-span-7 md:col-start-4",
    ratio: "aspect-[4/5]",
  },
];

export const WorkPortfolio = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [80, -80]);

  return (
    <section
      id="portfolio"
      ref={ref}
      className="relative border-t hairline py-24 md:py-40 overflow-hidden"
      data-testid="portfolio-section"
    >
      <div className="grid grid-cols-12 px-6 md:px-10 lg:px-14 gap-6 md:gap-10">
        <div className="col-span-12 md:col-span-3 label">04 — Work Portfolio</div>

        <div className="col-span-12 md:col-span-9">
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="label mb-6"
          >
            Recent Frames
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: EASE, delay: 0.1 }}
            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[1.02] text-ink max-w-4xl"
          >
            A folio of campaigns,{" "}
            <span className="italic">shoots &amp; brand stories</span> —
            considered, edited, and always in motion.
          </motion.h2>
        </div>
      </div>

      {/* Editorial Gallery Grid */}
      <div className="mt-16 md:mt-24 px-6 md:px-10 lg:px-14">
        <div
          className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8"
          data-testid="portfolio-gallery"
        >
          {works.map((w, i) => (
            <motion.figure
              key={w.src}
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 1, ease: EASE, delay: (i % 3) * 0.08 }}
              className={`group relative col-span-1 ${w.span}`}
              data-testid={`portfolio-tile-${i}`}
            >
              <div className={`relative overflow-hidden ${w.ratio}`}>
                <img
                  src={w.src}
                  alt=""
                  loading="lazy"
                  className="w-full h-full object-cover gray-hover"
                />
              </div>
            </motion.figure>
          ))}
        </div>
      </div>

      {/* CTA + Topic breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.3 }}
        transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
        className="mt-20 md:mt-28 px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-6 md:gap-10 items-end"
      >
        <div className="col-span-12 md:col-span-6 md:col-start-4 order-2 md:order-1">
          <p className="font-sans text-base md:text-lg text-ink/80 leading-relaxed max-w-lg">
            The full case-book lives on Linktree — updated as the season
            unfolds. Campaigns for luxury beauty houses, editorial shoots,
            PR &amp; styling activations, and end-to-end brand builds.
          </p>

          <a
            href={PORTFOLIO_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="portfolio-cta"
            className="group mt-10 inline-flex items-center gap-4 border-t border-b hairline py-5 pr-6 pl-1 hover:pl-4 transition-[padding] duration-500"
          >
            <span className="font-serif italic text-3xl md:text-4xl">
              View the full folio
            </span>
            <ArrowUpRight
              className="w-6 h-6 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-500"
              strokeWidth={1}
            />
          </a>

          <div className="mt-6 flex items-center gap-3 label">
            <span>PDF · Curated Deck</span>
            <span aria-hidden="true">·</span>
            <span>Hosted on Linktree</span>
          </div>
        </div>

        <div className="col-span-12 md:col-span-3 md:col-start-10 order-1 md:order-2">
          <ul className="border-t hairline">
            {[
              ["Campaign design", "Concept → art direction → edit"],
              ["PR & press", "Editorial placements, gifting, seeding"],
              ["Brand builds", "Identity, tone, digital experience"],
              ["Growth & ads", "Meta, Google, creator-led media"],
            ].map(([k, v]) => (
              <li
                key={k}
                className="border-b hairline py-4"
                data-testid={`portfolio-topic-${k
                  .replace(/\s+/g, "-")
                  .toLowerCase()}`}
              >
                <div className="font-serif italic text-lg md:text-xl">{k}</div>
                <div className="label mt-1">{v}</div>
              </li>
            ))}
          </ul>
        </div>
      </motion.div>

      <motion.span
        style={{ y }}
        aria-hidden="true"
        className="pointer-events-none absolute -left-4 md:left-4 bottom-6 font-serif italic text-[28vw] leading-none text-ink/[0.05] select-none"
      >
        folio
      </motion.span>
    </section>
  );
};

export default WorkPortfolio;
